import { DataModelAttributeType } from '@flowx/designer/modules/core/dictionary/data-model.dictionary'

/**
 * View-model for the AI "Edit Generated Data Model Keys" modal + summary, shared by the AI Designer
 * and AI Developer agents. `status` and `source` are independent axes: `status` drives the badge
 * (Used/Created), `source` the coloured marker + legend (process/project). The modal/summary are
 * presentational — each agent's adapter maps its domain object to/from `AiDmKeyRow` via `ref`.
 */

// Badge: a key reused as-is (Used) vs one created on Keep (Created). Project references are Created.
export const AiDmKeyRowStatus = {
  Used: 'used',
  Created: 'created',
} as const
export type AiDmKeyRowStatus = (typeof AiDmKeyRowStatus)[keyof typeof AiDmKeyRowStatus]

// Where the key originates: process or project (library deferred).
export const AiDmKeyRowSource = {
  Process: 'process',
  Project: 'project',
} as const
export type AiDmKeyRowSource = (typeof AiDmKeyRowSource)[keyof typeof AiDmKeyRowSource]

export type AiDmKeyRow<TRef = unknown> = {
  id: string
  key: string
  label?: string
  type: DataModelAttributeType
  multiInstance?: boolean
  status: AiDmKeyRowStatus
  source: AiDmKeyRowSource
  active: boolean
  description?: string
  // Parent row id for nested designer attributes; drives indentation + cascade opt-out.
  parentId?: string | null
  // `false` locks the key path (e.g. a project reference). Default true.
  keyEditable?: boolean
  // Set when a reference can't be resolved against the catalogue; shows an error and blocks save
  // until the row is opted out.
  unresolved?: boolean
  // Present → the key field is an flx-data-model-autocomplete (designer); absent → a plain input.
  keyAutocomplete?: { componentIdentifier?: string }
  // Opaque passthrough so the adapter can rebuild its own domain object.
  ref?: TRef
}

// Modal result (matches the showModalAboveAiPanel `data$` contract).
export type AiDmModifyModalResult = {
  confirm: boolean
  rows?: AiDmKeyRow[]
}

export const AI_DM_STATUS_BADGE: Record<
  AiDmKeyRowStatus,
  { label: string; tooltip: string; class: string }
> = {
  [AiDmKeyRowStatus.Used]: {
    label: 'Used',
    tooltip: 'This component will be mapped to an existing data key',
    class: 'bg-yellow-800',
  },
  [AiDmKeyRowStatus.Created]: {
    label: 'Created',
    tooltip: 'A new data key will be created for this component',
    class: 'bg-blue-800',
  },
}

// Source → marker colour: process grey, project purple.
export const AI_DM_SOURCE_MARKER: Record<AiDmKeyRowSource, string> = {
  [AiDmKeyRowSource.Process]: 'bg-neutral-400',
  [AiDmKeyRowSource.Project]: 'bg-purple-400',
}

// Legend rows in the modal footer, in display order.
export const AI_DM_SOURCE_LEGEND: { source: AiDmKeyRowSource; label: string }[] = [
  { source: AiDmKeyRowSource.Project, label: 'Project Data Model' },
  { source: AiDmKeyRowSource.Process, label: 'Process Data Model' },
]

export const AI_DM_REFERENCE_UNRESOLVED_MESSAGE =
  'This referenced object no longer exists; a new local key will be created instead'

export type { DataModelAttributeType }
